import { z } from 'zod';
import { optionalNumber } from './OptionalNumber';
import { optionalString } from './OptionalString';

const recurrenceIntervals = [
  'daily',
  'weekly',
  'monthly',
  'yearly',
] as const;

// These are the values the backend expects
const daysOfWeekValues = [
  'Sun',
  'Mon',
  'Tue',
  'Wed',
  'Thu',
  'Fri',
  'Sat',
] as const;

const monthValues = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
] as const;

const dayOfMonthValues = [
  '1', '2', '3', '4',
  '5', '6', '7', '8',
  '9', '10', '11', '12',
  '13', '14', '15', '16',
  '17', '18', '19', '20',
  '21', '22', '23', '24',
  '25', '26', '27', '28',
  '29', '30', '31',
] as const;

const occursOnDayValues = ['first', 'second', 'third', 'fourth', 'last'] as const;

export const RecurrenceIntervals = z.enum(recurrenceIntervals);
export const DaysOfWeek = z.enum(daysOfWeekValues);
export const DaysOfMonth = z.enum(dayOfMonthValues);
export const OccursOnDay = z.enum(occursOnDayValues);
export const Months = z.enum(monthValues);

export type DayOfWeek = z.infer<typeof DaysOfWeek>;
export type Month = z.infer<typeof Months>;
export type RecurrenceInterval = z.infer<typeof RecurrenceIntervals>;
export type DayOfMonth = z.infer<typeof DaysOfMonth>;
export type WeekOfMonth = z.infer<typeof OccursOnDay>;

export const optionalDate = z.string()
  .datetime({ offset: true })
  .optional()
  .or(z.literal(null))
  .or(z.literal(''));

export const scheduleSchema = z.object({
  id: optionalNumber(),
  startDateTime: optionalDate,
  interval: RecurrenceIntervals,
  selectedDays: z.array(DaysOfWeek).optional().default(() => []),
  selectedDay: DaysOfWeek.optional().nullable(),
  selectedMonth: Months.optional().nullable(),
  occurrences: optionalNumber(),
  occursEvery: optionalNumber(),
  occursOnDay: OccursOnDay.optional().nullable(),
  occursOnDayOfMonth: DaysOfMonth.optional(),
  endDate: optionalDate,
  // key is a frontend-only field used to track objects in lieu of a PK ID
  key: optionalString.default(() => crypto.randomUUID()),
});

export const existingScheduleSchema = scheduleSchema.extend({
  id: z.number(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});


type ScheduleField = keyof Schedule;
type RequireOmit = {
  required: ScheduleField[];
  omit: ScheduleField[];
}
type MonthlyYearly = {
  fixed: RequireOmit;
  variable: RequireOmit;
}

type FieldRequirements = {
  daily: RequireOmit,
  weekly: RequireOmit,
  monthly: MonthlyYearly,
  yearly: MonthlyYearly,
}

const baseMonthlyOmits: ScheduleField[] = ['selectedDays', 'selectedMonth', 'occursEvery'];
const baseYearlyOmits: ScheduleField[] = ['selectedDays', 'occursEvery'];

const requirements: FieldRequirements = {
  daily: {
    omit: ['occursOnDay', 'occursOnDayOfMonth', 'selectedDay', 'selectedDays', 'selectedMonth'],
    required: ['occursEvery'],
  },
  weekly: {
    omit: ['occursEvery', 'occursOnDay', 'occursOnDayOfMonth', 'selectedDay', 'selectedMonth'],
    required: ['selectedDays'],
  },
  monthly: {
    fixed: {
      omit: [...baseMonthlyOmits, 'selectedDay', 'occursOnDay'],
      required: ['occursOnDayOfMonth'],
    },
    variable: {
      omit: [...baseMonthlyOmits, 'occursOnDayOfMonth'],
      required: ['selectedDay', 'occursOnDay'],
    },
  },
  yearly: {
    fixed: {
      omit: [...baseYearlyOmits, 'selectedDay', 'occursOnDay'],
      required: ['occursOnDayOfMonth', 'selectedMonth'],
    },
    variable: {
      omit: [...baseYearlyOmits, 'occursOnDayOfMonth'],
      required: ['occursOnDay', 'selectedDay', 'selectedMonth'],
    },
  },
};

const clearUnusedFields = (fields: ScheduleField[], input: Schedule) => {
  const transformer = scheduleSchema
    .transform(schedule => {
      fields.forEach(field => {
        if (Array.isArray(schedule[field])) {
          schedule[field] = [];
        } else {
          schedule[field] = undefined;
        }
      });

      return schedule;
    });

  return transformer.parse(input);
};

const getRequiredAndOmittedFields = (interval: RecurrenceInterval, monthlyOption: RecurrenceOption, yearlyOption: RecurrenceOption): [required: ScheduleField[], omit: ScheduleField[]] => {
  const required: ScheduleField[] = [];
  const omitted: ScheduleField[] = [];

  if (interval === 'daily' || interval === 'weekly') {
    required.push(...requirements[interval].required);
    omitted.push(...requirements[interval].omit);
  }

  if (interval === 'monthly') {
    required.push(...requirements.monthly[monthlyOption].required);
    omitted.push(...requirements.monthly[monthlyOption].omit);
  }

  if (interval === 'yearly') {
    required.push(...requirements.yearly[yearlyOption].required);
    omitted.push(...requirements.yearly[yearlyOption].omit);
  }

  return [required, omitted];
};

export const cleanSchedule = (schedule: Schedule, monthlyOption: RecurrenceOption, yearlyOption: RecurrenceOption) => {
  const { interval } = schedule;

  const [, unusedFields] = getRequiredAndOmittedFields(interval, monthlyOption, yearlyOption);

  return clearUnusedFields(unusedFields, schedule);
};

export const validateSchedule = (testSchedule: Schedule, monthlyOption: RecurrenceOption, yearlyOption: RecurrenceOption) => {
  const { interval } = testSchedule;
  const [requiredFields, omittedFields] =getRequiredAndOmittedFields(interval, monthlyOption, yearlyOption);

  const validator = scheduleSchema.superRefine((schedule, ctx) => {
    requiredFields.forEach(field => {
      if (!schedule[field]) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `For a ${interval} interval, ${field} is required`,
          path: [field],
        });
      }
    });

    omittedFields.forEach(field => {
      if ((schedule[field] && !Array.isArray(schedule[field])) || (Array.isArray(schedule[field]) && schedule[field].length > 0)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `For a ${interval} interval, ${field} should not be set`,
          path: [field],
        });
      }
    });
  });

  const cleaned = cleanSchedule(testSchedule, monthlyOption, yearlyOption);

  validator.parse(cleaned);
};

export type NewSchedule = z.infer<typeof scheduleSchema>;
export type Schedule = z.infer<typeof existingScheduleSchema>;
export type RecurrenceOption = 'fixed' | 'variable';
